import { Grid, Theme, Typography } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { useRouter } from 'next/router'
import { makeStyles } from '@material-ui/core/styles'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import { Skeleton } from '@material-ui/lab'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import { SpeciesItem, AssayTypeItem } from '@src/types/graphql-types'
import { checkForDefaultAssay } from '@src/routes/Configurators/products/Milliplex/utils'

const useStyles = makeStyles((theme: Theme) => {
  return {
    researchAreasHeaderStyle: {
      margin: theme.spacing(4, 0, 6, 0),
      fontSize: '16px',
    },
    checkboxContainer: {
      marginLeft: theme.spacing(2),
    },
  }
})

export interface ResearchAreasProps {
  listResearchAreas: SpeciesItem[]
  loading: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  assayTypeList: AssayTypeItem[]
}

const ResearchAreas: React.FC<ResearchAreasProps> = ({
  listResearchAreas,
  loading,
  setDisplayPageError,
  assayTypeList,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const formik = useFormikContext<MilliplexFormValues>()
  const researchAreaList =
    listResearchAreas.find((single) => single.species === formik.values.species)
      ?.researchAreasList || []
  const onChange = (e) => {
    const checked = e.target.checked,
      value = e.target.value
    const filteredValues = formik.values.researchAreas.filter(
      (area) => area !== value
    )
    checked && filteredValues.push(value)

    const assayDefault = checkForDefaultAssay({
      assayTypeList: assayTypeList,
      species: formik.values.species,
      researchAreas: filteredValues,
    })

    formik.setValues({
      ...formik.values,
      researchAreas: filteredValues,
      assay: assayDefault,
      analytes: [],
    })

    if (assayDefault !== '') formik.setFieldTouched('assay', false)
    formik.setFieldTouched('assay', false)
    setDisplayPageError(false)

    const assayQuery = assayDefault !== '' ? { assay: assayDefault } : {}

    router.replace(
      {
        pathname: router.pathname,
        query: {
          activeLink: '',
          country: router.query?.country,
          language: router.query?.language,
          researchAreas: filteredValues,
          species: router.query?.species,
          ...assayQuery,
        },
      },
      undefined,
      { shallow: true }
    )
  }

  return (
    <div>
      <Typography
        variant="subtitle2"
        className={classes.researchAreasHeaderStyle}
      >
        <FormattedMessage
          id="RESEARCH_AREAS_OPTIONAL"
          defaultMessage="Research Areas (optional)"
        />
      </Typography>
      {loading && researchAreaList?.length === 0 && (
        <Grid container spacing={2}>
          {Array(3)
            .fill(undefined)
            .map((_, index) => (
              <Grid item xs={4} key={index}>
                <Skeleton animation="wave" height={40} width="100%" />
              </Grid>
            ))}
        </Grid>
      )}
      <Grid container spacing={2} className={classes.checkboxContainer}>
        {researchAreaList?.map(
          (area) =>
            area && (
              <Grid item xs={12} sm={6} md={4} key={area}>
                <LiquidCheckbox
                  aria-label={area}
                  value={area}
                  checked={formik.values.researchAreas.some(
                    (single) => single === area
                  )}
                  onChange={onChange}
                  label={area}
                />
              </Grid>
            )
        )}
      </Grid>
      {!loading && formik.values.species === '' && (
        <Typography variant="body2">
          <FormattedMessage
            id="SELECT_A_SPECIES_TO_VIEW_RESEARCH_AREAS"
            defaultMessage="Select a species to view research areas."
          />
        </Typography>
      )}
    </div>
  )
}

export default ResearchAreas
